import { ISubscription } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { useMemo } from 'react';
import { create as createStore } from 'zustand';
import { persist } from 'zustand/middleware';

import { useProfile } from '../profile';

import { sdk } from '@/services';

interface IStore {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  lastFourIdsByUser: Record<string, string[]>;
  selectedSubscription?: ISubscription;
  setSelectedSubscription: (subscriptionSelected?: ISubscription) => void;
}

const SubscriptionService = sdk.getService('SubscriptionService');

const store = createStore<IStore>()(
  persist(
    set => ({
      isOpen: false,
      setIsOpen: (isOpen: boolean) => set({ isOpen }),
      lastFourIdsByUser: {},
      setSelectedSubscription: (selectedSubscription?: ISubscription) => {
        set(state => {
          if (selectedSubscription) {
            const userId = selectedSubscription.user?._id;
            if (!userId) {
              return { selectedSubscription };
            }
            const previousIds = state.lastFourIdsByUser[userId] || [];
            const updatedIds = [
              selectedSubscription._id,
              ...previousIds.filter(id => id !== selectedSubscription._id)
            ].slice(0, 4);

            Cookies.set('selectedSubscriptionId', selectedSubscription._id, { expires: 365 });

            return {
              selectedSubscription,
              lastFourIdsByUser: {
                ...state.lastFourIdsByUser,
                [userId]: updatedIds
              }
            };
          }
          return { selectedSubscription };
        });
      }
    }),
    {
      name: 'subscription-storage',
      partialize: state => ({ lastFourIdsByUser: state.lastFourIdsByUser })
    }
  )
);

export const useMySubscriptions = () => {
  const { profile } = useProfile();
  const userId = profile?._id;
  const { selectedSubscription, setSelectedSubscription, isOpen, setIsOpen, lastFourIdsByUser } =
    store();

  const { isLoading, data, error } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => SubscriptionService.getMySubscriptions(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!profile
  });

  const filteredSubscriptions = useMemo(() => data?.filter(s => !s.isHidden), [data]);

  const lastFourSubscriptions = useMemo(() => {
    if (!filteredSubscriptions) return [] as ISubscription[];
    const ids = userId ? lastFourIdsByUser[userId] || [] : [];

    const selectedList = ids
      .map(id => filteredSubscriptions.find(s => s._id === id))
      .filter((s): s is ISubscription => Boolean(s));

    if (selectedList.length >= 4) return selectedList;

    const needed = 4 - selectedList.length;
    const filler = filteredSubscriptions.filter(s => !selectedList.some(sel => sel._id === s._id));

    return [...selectedList, ...filler.slice(0, needed)];
  }, [lastFourIdsByUser, filteredSubscriptions, userId]);

  const subscriptionsByTag = useMemo(() => {
    if (!filteredSubscriptions) return {} as Record<string, ISubscription[]>;

    const groupedByTag = {} as Record<string, ISubscription[]>;
    const userTagIds = new Set((profile?.tags || []).map(tag => tag._id));

    filteredSubscriptions.forEach(subscription => {
      subscription.course.tags
        .filter(tag => userTagIds.has(tag._id))
        .forEach(tag => {
          if (!groupedByTag[tag.name]) {
            groupedByTag[tag.name] = [];
          }
          groupedByTag[tag.name].push(subscription);
        });
    });

    return groupedByTag;
  }, [filteredSubscriptions, profile?.tags]);

  const planificableSubscriptions = useMemo(
    () => filteredSubscriptions?.filter(s => s.course.isPlanificable),
    [filteredSubscriptions]
  );

  return {
    isOpen,
    setIsOpen,
    error,
    isLoading,
    subscriptions: data,
    selectedSubscription,
    filteredSubscriptions,
    subscriptionsByTag,
    planificableSubscriptions,
    lastFourSubscriptions,
    setSelectedSubscription
  };
};
